import { type } from 'arktype';

// ---------------------------------------------------------------------------
// Update notification preferences (bulk upsert)
// ---------------------------------------------------------------------------

/**
 * Single preference entry for upsert.
 * - `event_type`: specific event type or `'*'` for wildcard
 * - `channel`: one of the supported channel types
 * - `enabled`: whether the channel is enabled for this event type
 */
export const preferenceEntrySchema = type({
  event_type: type('string >= 1').configure({
    description: "Notification event type or '*' for a wildcard that matches all events.",
    examples: ['assignment.published'],
  }),
  channel: type("'in_app' | 'webhook' | 'email' | 'mobile_push' | 'slack' | 'teams'").configure({
    description:
      "Delivery channel. 'in_app' and 'mobile_push' are user-facing; 'webhook', 'slack', and 'teams' are outbound integrations; 'email' is SMTP.",
    examples: ['email'],
  }),
  enabled: type('boolean').configure({
    description: 'Whether this channel is enabled for the event type.',
  }),
}).configure({ description: 'A single notification preference for one event/channel pair.' });

export type PreferenceEntry = typeof preferenceEntrySchema.t;

export const updatePreferencesSchema = type({
  preferences: preferenceEntrySchema.array().configure({
    description: 'List of preference entries to upsert atomically. Entries not in the list are left unchanged.',
  }),
}).configure({ description: "Bulk-upsert payload for the current user's notification preferences." });

export type UpdatePreferences = typeof updatePreferencesSchema.t;

// ---------------------------------------------------------------------------
// Update channel configs (bulk upsert — admin only)
// ---------------------------------------------------------------------------

/**
 * Single channel config entry for upsert.
 * - `channel`: one of the supported channel types
 * - `event_type`: specific event type or `'*'` for wildcard
 * - `enabled`: whether this channel is enabled for the event type
 * - `config`: optional JSONB channel-specific settings
 */
export const channelConfigEntrySchema = type({
  channel: type("'in_app' | 'webhook' | 'email' | 'mobile_push' | 'slack' | 'teams'").configure({
    description: 'Delivery channel this tenant-level configuration targets.',
    examples: ['slack'],
  }),
  event_type: type('string >= 1').configure({
    description: "Notification event type or '*' for a wildcard matching all events.",
    examples: ['*'],
  }),
  enabled: type('boolean').configure({
    description: 'Whether the channel is enabled for this event type at the tenant level.',
  }),
  'config?': type('Record<string, unknown> | null').configure({
    description: 'Channel-specific JSONB settings (webhook URL, Slack channel, etc.). `null` clears any previously stored config.',
  }),
}).configure({ description: 'Tenant-level channel configuration entry (admin-managed).' });

export type ChannelConfigEntry = typeof channelConfigEntrySchema.t;

export const updateChannelConfigsSchema = type({
  configs: channelConfigEntrySchema.array().configure({
    description: 'List of channel configurations to upsert atomically.',
  }),
}).configure({ description: 'Bulk-upsert payload for tenant-level notification channel configurations.' });

export type UpdateChannelConfigs = typeof updateChannelConfigsSchema.t;

// ---------------------------------------------------------------------------
// Response interfaces
// ---------------------------------------------------------------------------

export interface PreferenceResponse {
  id: string;
  userId: string;
  eventType: string;
  channel: string;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ChannelConfigResponse {
  id: string;
  channel: string;
  eventType: string;
  enabled: boolean;
  config: Record<string, unknown> | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// Notification response interfaces
// ---------------------------------------------------------------------------

export interface NotificationResponse {
  id: string;
  eventId: string;
  recipientId: string;
  title: string;
  message: string;
  type: string;
  priority: string;
  category: string | null;
  metadata: Record<string, unknown> | null;
  read: boolean;
  readAt: string | null;
  archived: boolean;
  expiresAt: string | null;
  createdAt: string;
}

export interface DeliveryResponse {
  id: string;
  notificationId: string;
  channel: string;
  status: string;
  attemptCount: number;
  maxAttempts: number;
  nextAttemptAt: string | null;
  lastAttemptAt: string | null;
  deliveredAt: string | null;
  errorMessage: string | null;
  responseData: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}
